import * as React from 'react';
import { Button } from '@/components/ui/button';

export interface SignaturePadHandle {
  /** True until a stroke is drawn (or after Clear). */
  isEmpty(): boolean;
  /** `data:image/png;base64,...` of the drawing, or null when empty. */
  toPng(): string | null;
  clear(): void;
}

/**
 * A drawn signature on a `<canvas>`, by pointer events (finger, pen and mouse alike). The backing store is the
 * CSS size times devicePixelRatio and the context is scaled by it, so the line is crisp on a 3x phone while the
 * coordinates stay in CSS pixels. `touch-action: none` keeps a finger stroke from scrolling the page.
 */
export const SignaturePad = React.forwardRef<
  SignaturePadHandle,
  { label: string; clearLabel: string; hint?: string; onChange?: (empty: boolean) => void }
>(function SignaturePad({ label, clearLabel, hint, onChange }, ref) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const ctxRef = React.useRef<CanvasRenderingContext2D | null>(null);
  const drawing = React.useRef<number | null>(null);
  const last = React.useRef<{ x: number; y: number } | null>(null);
  const strokes = React.useRef(0);
  const hintId = React.useId();

  const setup = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx;
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // The ink token, read from the page (never a literal colour).
    ctx.strokeStyle = getComputedStyle(canvas).color || 'black';
    ctx.fillStyle = ctx.strokeStyle;
  }, []);

  React.useEffect(() => {
    setup();
    // A resize (rotation) re-creates the backing store, which wipes it: start over rather than keep a
    // stretched drawing.
    const onResize = () => {
      setup();
      if (strokes.current > 0) {
        strokes.current = 0;
        onChange?.(true);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setup, onChange]);

  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: (e.clientX ?? 0) - rect.left, y: (e.clientY ?? 0) - rect.top };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Older engines: drawing still works while the pointer stays inside.
    }
    drawing.current = e.pointerId;
    const p = point(e);
    last.current = p;
    const ctx = ctxRef.current;
    if (ctx) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    if (strokes.current++ === 0) onChange?.(false);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (drawing.current !== e.pointerId || !last.current) return;
    const p = point(e);
    const ctx = ctxRef.current;
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    last.current = p;
  }

  function end() {
    drawing.current = null;
    last.current = null;
  }

  const clear = React.useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (canvas && ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
    strokes.current = 0;
    onChange?.(true);
  }, [onChange]);

  React.useImperativeHandle(
    ref,
    () => ({
      isEmpty: () => strokes.current === 0,
      toPng: () => (strokes.current === 0 || !canvasRef.current ? null : canvasRef.current.toDataURL('image/png')),
      clear,
    }),
    [clear],
  );

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        aria-describedby={hint ? hintId : undefined}
        className="h-40 w-full touch-none rounded-md border border-dashed border-hairline-strong bg-card text-ink"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
      />
      <div className="flex items-center justify-between gap-3">
        {hint ? (
          <p id={hintId} className="text-sm text-ink-muted">
            {hint}
          </p>
        ) : (
          <span />
        )}
        <Button type="button" variant="outline" size="sm" onClick={clear}>
          {clearLabel}
        </Button>
      </div>
    </div>
  );
});
